import styled from '@emotion/styled';
import { breakpoints, Fonts, mediaQuery, Spacings } from '@styles/index';
import H4 from '@base/H4/H4';
import P1 from '@base/P1/P1';
import Paper from '@base/Paper/Paper';
import LiveClasses from 'aquilla/icons/features/LiveClasses';
import TestIcon from 'aquilla/icons/features/Test';
import P2 from '@base/P2/P2';
import H6 from '@base/H6/H6';
import { wordsToSentence } from '@utils/textHelper';
import BatchLanguages from '@icon/Languages';
import Tooltip from 'aquilla/core/molecules/Tooltip';
import Info from 'aquilla/icons/actions/Info';
import Typography from 'aquilla/core/atoms/Typography';
import BottomSheet from '@comp/BottomSheet/BottomSheet';
import { useState } from 'react';
import PartnerBadge from 'aquilla/icons/levels/PartnerBadge';
import { useIsMobile } from '@stores/AppCommonData';
import BatchAccessEnded from 'aquilla/icons/unacademyStore/BatchAccessEnded';
import useBatchSkuDetails from '@api/hooks/batch/useBatchSkuDetails';
import {
  BATCH_CONTENT_TYPE_MAP,
  getHasStorePurchase,
  getIsPartnerEducatorBatch
} from './utils';
import BatchBrochureDownload from './BatchBrochureDownload';

const Wrapper = styled.div`
  display: grid;
  grid-template-columns: repeat(24, 1fr);
  grid-column-gap: ${Spacings.SPACING_4B};
  grid-column: span 24;
  position: relative;
  margin-top: ${({ $isEnrolled }) =>
    $isEnrolled ? Spacings.SPACING_12B : Spacings.SPACING_0B};
  ${mediaQuery.uptoMobile} {
    display: block;
    margin-bottom: 0px;
    grid-column: span 10;
    grid-template-columns: repeat(10, 1fr);
    margin-top: ${({ $isEnrolled }) =>
      $isEnrolled ? Spacings.SPACING_8B : Spacings.SPACING_0B};
  }
`;

const LeftWrapper = styled.div`
  grid-column: 2/8;
  position: sticky;
  top: 235px;
  z-index: 1;
  ${mediaQuery.uptoMobile} {
    position: relative;
    top: 0;
    padding: 0;
    grid-column: span 10;
    margin-bottom: ${Spacings.SPACING_6B};
  }
`;

const StyledP1 = styled(P1)`
  color: var(--color-text-secondary);
  margin-top: ${Spacings.SPACING_2B};
  margin-bottom: ${Spacings.SPACING_4B};
  max-width: 95%;
  ${mediaQuery.uptoMobile} {
    font-size: ${Fonts.P2};
    line-height: ${Spacings.SPACING_5B};
  }
`;

const RightWrapper = styled.div`
  grid-column: 9/25;
  display: grid;
  grid-template-columns: repeat(2, 2fr);
  grid-column-gap: ${Spacings.SPACING_4B};
  grid-row-gap: ${Spacings.SPACING_4B};
  margin-bottom: ${Spacings.SPACING_2B};
  @media only screen and (max-width: ${breakpoints.mobile}) {
    grid-column: span 10;
    grid-template-columns: repeat(1, 1fr);
    & > a {
      grid-column: span 10;
      margin-bottom: 0;
    }
  }
`;

const StatWrapper = styled(Paper)`
  width: 368px;
  height: 88px;
  border-radius: ${Spacings.SPACING_1B};
  position: relative;

  padding: ${Spacings.SPACING_4B};
  padding-left: ${Spacings.SPACING_6B};
  display: flex;
  align-items: center;
  div:nth-child(3) {
    margin-left: ${Spacings.SPACING_7B};
  }
  ${mediaQuery.uptoMobile} {
    width: 100%;
    height: 72px;
  }
`;

const StatContent = styled.div`
  margin-left: ${Spacings.SPACING_3B};
`;

const StyledP2 = styled(P2)`
  color: var(--color-text-secondary);
  margin-bottom: ${Spacings.SPACING_2};
`;

const StyledH6 = styled(H6)`
  ${mediaQuery.uptoMobile} {
    font-size: ${Fonts.H6};
  }
`;

const StyledPartnerEducator = styled(StyledH6)`
  display: flex;
  align-items: center;
`;

const InfoIconWrapper = styled.div`
  height: 20px;
  ${mediaQuery.uptoMobile} {
    height: 18px;
  }
`;

const Divider = styled.div`
  height: ${Spacings.SPACING_10B};
`;

const DividerLine = styled.div`
  position: absolute;
  width: ${Spacings.SPACING_6B};
  height: 2px;
  left: calc(50% - 12px);
  top: ${Spacings.SPACING_6B};
  background: var(--color-i-gray-13);
  border-radius: 1px;
`;

const BottomSheetHeading = styled(Typography)`
  margin-bottom: ${Spacings.SPACING_2B};
`;

const ToolTipWrapper = styled.div`
  height: 20px;
  margin-left: ${Spacings.SPACING_1B};
`;

const StyledTooltip = styled(Tooltip)`
  ${mediaQuery.uptoMobile} {
    &:hover {
      div:last-of-type {
        visibility: hidden;
      }
    }
  }
`;

const BatchAbout = ({
  batch,
  batchUID,
  isPurchased,
  isRecordedOrHybrid,
  accessEndDate,
  isDurationBased,
  displayDuration
}) => {
  const {
    numberOfLiveClasses,
    numberOfMockTests,
    numberOfPractice,
    numberOfRecordedClasses,
    languages,
    contentType,
    quizzesCount,
    lessonsCount,
    isEnrolled
  } = batch || {};
  const { properties } = batch?.nextItem || {};
  const { isOffline = false, isLiveStreamingEnabled = true } = properties || {};
  const isStreamingDisabled = isOffline && !isLiveStreamingEnabled;
  const batchGoalUID = batch?.goal?.uid;
  const { batchSkuDetails } = useBatchSkuDetails(batchGoalUID, batchUID);
  const hasStorePurchase = getHasStorePurchase(batchUID, batchSkuDetails);

  const [showPartnerInfo, setShowPartnerInfo] = useState(false);
  const isMobile = useIsMobile();

  const togglePartnerInfoBS = () => {
    if (isMobile) setShowPartnerInfo(!showPartnerInfo);
  };

  const isPartnerBatch = getIsPartnerEducatorBatch(batch);

  const languagesText = wordsToSentence(languages, 'label', ', ', ', ');

  const isOldBatch =
    !numberOfLiveClasses &&
    !numberOfMockTests &&
    !numberOfPractice &&
    !numberOfRecordedClasses;

  const getAccessText = () => {
    if (isDurationBased) {
      return hasStorePurchase
        ? 'Access available till'
        : 'Access available for';
    }
    return 'Access ends on';
  };

  return (
    <Wrapper $isEnrolled={isEnrolled}>
      <LeftWrapper>
        <H4>About</H4>
        <StyledP1>
          All the learning material you get when you join this batch
        </StyledP1>
        {!isMobile && (
          <BatchBrochureDownload
            batchUID={batchUID}
            isPurchased={isPurchased}
          />
        )}
      </LeftWrapper>
      <RightWrapper>
        {!isOldBatch && (
          <>
            {((numberOfLiveClasses > 0 &&
              contentType !== BATCH_CONTENT_TYPE_MAP.RECORDED) ||
              (numberOfRecordedClasses > 0 &&
                contentType !== BATCH_CONTENT_TYPE_MAP.LIVE)) && (
              <StatWrapper>
                <LiveClasses />
                {numberOfLiveClasses > 0 &&
                  contentType !== BATCH_CONTENT_TYPE_MAP.RECORDED && (
                    <StatContent>
                      <StyledP2>
                        {isStreamingDisabled
                          ? 'Centre classes'
                          : 'Live classes'}
                      </StyledP2>
                      <StyledH6>{numberOfLiveClasses}</StyledH6>
                    </StatContent>
                  )}
                {numberOfRecordedClasses > 0 &&
                  contentType !== BATCH_CONTENT_TYPE_MAP.LIVE && (
                    <StatContent>
                      <StyledP2>Recorded classes</StyledP2>
                      <StyledH6>{numberOfRecordedClasses}</StyledH6>
                    </StatContent>
                  )}
              </StatWrapper>
            )}
            {(numberOfMockTests > 0 || numberOfPractice > 0) && (
              <StatWrapper>
                <TestIcon />
                {numberOfMockTests > 0 && (
                  <StatContent>
                    <StyledP2>Mock tests</StyledP2>
                    <StyledH6>{numberOfMockTests}</StyledH6>
                  </StatContent>
                )}
                {numberOfPractice > 0 && (
                  <StatContent>
                    <StyledP2>Practices</StyledP2>
                    <StyledH6>{numberOfPractice}</StyledH6>
                  </StatContent>
                )}
              </StatWrapper>
            )}
          </>
        )}
        {isOldBatch && (
          <>
            {lessonsCount > 0 && (
              <StatWrapper>
                <LiveClasses />
                <StatContent>
                  <StyledP2>
                    {isRecordedOrHybrid ? 'Recorded classes' : 'Live classes'}
                  </StyledP2>
                  <StyledH6>{lessonsCount}</StyledH6>
                </StatContent>
              </StatWrapper>
            )}

            {quizzesCount > 0 && (
              <StatWrapper>
                <TestIcon />
                <StatContent>
                  <StyledP2>Mock tests</StyledP2>
                  <StyledH6>{quizzesCount}</StyledH6>
                </StatContent>
              </StatWrapper>
            )}
          </>
        )}
        {languagesText && (
          <StatWrapper>
            <BatchLanguages size={40} />
            <StatContent>
              <StyledP2>Language of teaching</StyledP2>
              <StyledH6>{languagesText}</StyledH6>
            </StatContent>
          </StatWrapper>
        )}
        {isPartnerBatch && (
          <StatWrapper>
            <PartnerBadge width={40} height={40} />
            <StatContent>
              <StyledP2>Educator</StyledP2>
              <StyledPartnerEducator>
                Partner educator
                <ToolTipWrapper onClick={togglePartnerInfoBS}>
                  {isMobile ? (
                    <InfoIconWrapper>
                      <Info
                        width={20}
                        height={20}
                        color="var(--color-text-secondary)"
                      />
                    </InfoIconWrapper>
                  ) : (
                    <StyledTooltip
                      label="Partner educators are educators affiliated with Unacademy"
                      placement="bottom"
                      color="var(--color-base-fill)"
                    >
                      <InfoIconWrapper>
                        <Info
                          width={20}
                          height={20}
                          color="var(--color-text-secondary)"
                        />
                      </InfoIconWrapper>
                    </StyledTooltip>
                  )}
                </ToolTipWrapper>
              </StyledPartnerEducator>
            </StatContent>
            {isPartnerBatch && (
              <BottomSheet
                open={showPartnerInfo}
                onClose={togglePartnerInfoBS}
                icon={<></>}
                contentContainerStyle="padding-bottom: 16px;"
              >
                <Divider>
                  <DividerLine />
                </Divider>
                <BottomSheetHeading variant="h5">
                  Partner educators
                </BottomSheetHeading>
                <Typography variant="p2">
                  Partner educators are educators affiliated with Unacademy.
                </Typography>
              </BottomSheet>
            )}
          </StatWrapper>
        )}
        {accessEndDate && (
          <StatWrapper>
            <BatchAccessEnded width={40} height={40} />
            <StatContent>
              <StyledP2>{getAccessText()}</StyledP2>
              <StyledH6>
                {isDurationBased && !hasStorePurchase
                  ? displayDuration
                  : accessEndDate}
              </StyledH6>
            </StatContent>
          </StatWrapper>
        )}
      </RightWrapper>
    </Wrapper>
  );
};

export default BatchAbout;
